import { StatusData } from "@fairfleet/geotab";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { fetchStatusDataComplete } from "@/utilities/query/fetchStatusDataComplete";

/**
 * Every reading a vehicle sent over a period, across all its diagnostics.
 *
 * Through `fetchStatusDataComplete`, which pages forward until the window is covered. Geotab
 * returns readings oldest first and silently truncates at the results limit, so a single request
 * for a busy vehicle loses the newest — and most interesting — part of the period. That utility
 * is also where the retry and abort behaviour already lives.
 *
 * @param deviceId - The vehicle, or `undefined` while none is picked.
 * @param fromDate - Start of the period.
 * @param toDate - End of the period.
 */
export function useMotorStatusData(
  deviceId: string | undefined,
  fromDate: Date,
  toDate: Date,
) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    // Timestamps rather than the dates themselves: React Query hashes the key by value, and two
    // Date objects for the same instant do not hash alike.
    queryKey: [
      "motorStatusData",
      activeSessionId,
      deviceId,
      fromDate.getTime(),
      toDate.getTime(),
    ] as const,

    queryFn: async ({ signal }) => {
      const readings: StatusData[] = [];

      await fetchStatusDataComplete(geotabClient, {
        // One series, no diagnostic filter: the page is about finding out what a vehicle reports
        // at all, so it cannot name the diagnostics up front.
        tasks: [{ key: "all", deviceId: deviceId as string }],
        fromDate,
        toDate,
        signal,
        onPage: (_key, page) => readings.push(...page),
      });

      return readings.sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());
    },

    enabled: !!deviceId,
    placeholderData: keepPreviousData,
  });
}
